import type { ActionId } from '../../../services/actions/action'

export const ORBIT_SLICE_TO_ACTION: Record<string, ActionId> = {
  'message-reply': 'chat.message.reply',
  'message-copy': 'chat.message.copy',
  'message-task': 'chat.message.convert-to-task',
  'message-delete': 'chat.message.delete',
  'channel-rename': 'chat.channel.rename',
  'channel-notifications': 'chat.channel.notifications',
  'channel-leave': 'chat.channel.leave',
  'mention-profile': 'chat.mention.open-profile',
  'mention-dm': 'chat.mention.dm',

  // Checklist slices (U4.2)
  'node-toggle-done': 'checklist.node.toggle-done',
  'node-add-child': 'checklist.node.add-child',
  'node-link': 'checklist.node.link',
  'node-blockers': 'checklist.node.show-blockers',
  'node-delete': 'checklist.node.delete',
  'gate-toggle': 'checklist.gate.toggle',
  'gate-add-input': 'checklist.gate.add-input',
  'gate-inputs': 'checklist.gate.show-inputs',
  'gate-delete': 'checklist.gate.delete',
  'book-rename': 'checklist.book.rename',
  'book-duplicate': 'checklist.book.duplicate',
  'book-export': 'checklist.book.export',
  'book-delete': 'checklist.book.delete',
  'page-rename': 'checklist.page.rename',
  'page-duplicate': 'checklist.page.duplicate',
  'page-move': 'checklist.page.move',
  'page-delete': 'checklist.page.delete',
  'canvas-new-task': 'checklist.canvas.new-task',
  'canvas-new-gate': 'checklist.canvas.new-gate',
  'canvas-sort': 'checklist.canvas.sort',
  'canvas-blockers': 'checklist.canvas.show-blockers',

  // Design slices (U4.3)
  'node-edit': 'design.node.edit',
  'node-duplicate': 'design.node.duplicate',
  'node-change-role': 'design.node.change-role',
  'design:node-delete': 'design.node.delete',
  'role-cycle': 'design.role.cycle',
  'role-downstream': 'design.role.show-downstream',
  'role-detach': 'design.role.detach',
  'canvas-new-node': 'design.canvas.new-node',
  'canvas-reflow': 'design.canvas.reflow',
  'canvas-bindings': 'design.canvas.show-bindings',

  // Forms slices (U4.4)
  'field-rename': 'form.field.rename',
  'field-change-type': 'form.field.change-type',
  'field-required': 'form.field.toggle-required',
  'field-delete': 'form.field.delete',
  'section-rename': 'form.section.rename',
  'section-collapse': 'form.section.collapse',
  'section-delete': 'form.section.delete',
  'submit-rules': 'form.submit.rules',
  'submit-preview': 'form.submit.preview',
  'canvas-add-field': 'form.canvas.add-field',
  'canvas-add-section': 'form.canvas.add-section',
  'canvas-preview': 'form.canvas.preview',

  // Meeting slices (U4.5)
  'meeting-open': 'meeting.record.open',
  'meeting-duplicate': 'meeting.record.duplicate',
  'meeting-reschedule': 'meeting.record.reschedule',
  'meeting-delete': 'meeting.record.delete',
  'attendee-ping': 'meeting.attendee.ping',
  'attendee-assign': 'meeting.attendee.assign',
  'attendee-remove': 'meeting.attendee.remove',
  'agenda-edit': 'meeting.agenda.edit',
  'agenda-convert': 'meeting.agenda.convert-to-task',
  'agenda-delete': 'meeting.agenda.delete',
  'action-complete': 'meeting.action.complete',
  'action-assign': 'meeting.action.assign',
  'action-delete': 'meeting.action.delete',
  'canvas-new-meeting': 'meeting.canvas.new-meeting',
  'canvas-new-agenda-item': 'meeting.canvas.new-agenda-item',

  // Board slices (U4.6)
  'card-open': 'board.card.open',
  'card-move': 'board.card.move',
  'card-duplicate': 'board.card.duplicate',
  'card-delete': 'board.card.delete',
  'column-rename': 'board.column.rename',
  'column-reorder': 'board.column.reorder',
  'column-delete': 'board.column.delete',
  'cell-new': 'board.cell.new',
  'cell-paste': 'board.cell.paste',
  'cell-select': 'board.cell.select',
  'board-new-card': 'board.canvas.new-card',
  'board-new-column': 'board.canvas.new-column',

  // Timeline slices (U4.7)
  'event-edit': 'timeline.event.edit',
  'event-duplicate': 'timeline.event.duplicate',
  'event-band': 'timeline.event.move-to-band',
  'event-delete': 'timeline.event.delete',
  'band-rename': 'timeline.band.rename',
  'band-reorder': 'timeline.band.reorder',
  'band-collapse': 'timeline.band.collapse',
  'timeline-new': 'timeline.canvas.new-event',
  'timeline-fit': 'timeline.canvas.fit-to-range',

  // Dashboard slices (U4.8)
  'goal-rename': 'goal.rename',
  'goal-reprioritize': 'goal.reprioritize',
  'goal-link': 'goal.link',
  'goal-delete': 'goal.delete',
  'goal-connections': 'goal.show-connections',
  'task-complete': 'task.complete',
  'task-duplicate': 'task.duplicate',
  'task-move': 'task.move',
  'task-link': 'task.link',
  'task-delete': 'task.delete',
  'marker-edit': 'marker.edit',
  'marker-shift': 'marker.shift',
  'marker-dependencies': 'marker.show-dependencies',
  'marker-delete': 'marker.delete',
  'canvas-new-goal': 'dashboard.canvas.new-goal',
  'canvas-realign': 'dashboard.canvas.realign',
  'canvas-connections': 'dashboard.canvas.show-connections',
  'dashboard:canvas-new-task': 'dashboard.canvas.new-task',
}

export const ORBIT_PAGE_SLICE_TO_ACTION: Record<string, Record<string, ActionId>> = {
  checklist: {
    'node-delete': 'checklist.node.delete',
  },
  design: {
    'node-delete': 'design.node.delete',
  },
  dashboard: {
    'canvas-new-task': 'dashboard.canvas.new-task',
  },
}

export const resolveOrbitActionId = (
  sliceId: string,
  page?: string,
): ActionId | undefined => {
  if (page && ORBIT_PAGE_SLICE_TO_ACTION[page]?.[sliceId]) {
    return ORBIT_PAGE_SLICE_TO_ACTION[page][sliceId]
  }
  if (page && ORBIT_SLICE_TO_ACTION[`${page}:${sliceId}`]) {
    return ORBIT_SLICE_TO_ACTION[`${page}:${sliceId}`]
  }
  return ORBIT_SLICE_TO_ACTION[sliceId]
}

export const ORBIT_SLICES_INTENTIONALLY_UNMAPPED: readonly string[] = [
  'lcm-pin',
  'lcm-hide',
  'lcm-rename',
  'lcm-reset',
]
